"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColumnFilterQuery = exports.ColumnFilterMapSchema = exports.ColumnFilterSchema = exports.NumberColumnFilterSchema = exports.DateColumnFilterSchema = exports.ListColumnFilterSchema = exports.TextColumnFilterSchema = exports.CONDITION_LABEL = exports.COMPARE_LABEL = exports.CompareOp = void 0;
exports.columnFilterLabel = columnFilterLabel;
const zod_1 = require("zod");
/**
 * Bộ lọc trên đầu từng cột của lưới — dựng lại hàng `auxhead` của hệ cũ
 * (tree_task.zul, tree_task_unit.zul, tree_task_report_plan/perform.zul).
 *
 * Hệ cũ có đúng bốn kiểu ô lọc:
 *  - ô chữ gõ thẳng trên đầu cột, Enter là lọc (chứa chuỗi);
 *  - hộp chọn nhiều giá trị có tick + ô tìm, kèm nút "Lọc dữ liệu" / "Bỏ lọc";
 *  - khoảng ngày: `phép so sánh + ngày` [Và/Hoặc] `phép so sánh + ngày`;
 *  - khoảng số cho cột Tiến độ (%).
 *
 * Mã phép so sánh lấy nguyên của hệ cũ (code_master `code.common.compare`)
 * để hai hệ hiểu cùng một con số.
 */
exports.CompareOp = {
    GT: 1,
    GTE: 2,
    EQ: 3,
    LT: 4,
    LTE: 5,
};
exports.COMPARE_LABEL = {
    1: "Lớn hơn",
    2: "Lớn hơn hoặc bằng",
    3: "Bằng",
    4: "Nhỏ hơn",
    5: "Nhỏ hơn hoặc bằng",
};
/** code_master `code.common.conditions` */
exports.CONDITION_LABEL = { 1: "Và", 2: "Hoặc" };
const CompareCode = zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2), zod_1.z.literal(3), zod_1.z.literal(4), zod_1.z.literal(5)]);
const ConditionCode = zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2)]);
exports.TextColumnFilterSchema = zod_1.z.object({
    kind: zod_1.z.literal("text"),
    value: zod_1.z.string().min(1),
});
exports.ListColumnFilterSchema = zod_1.z.object({
    kind: zod_1.z.literal("list"),
    /** `null` là lựa chọn "(trống)" — hệ cũ cũng cho lọc theo ô không có dữ liệu. */
    values: zod_1.z.array(zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.null()])).min(1),
});
exports.DateColumnFilterSchema = zod_1.z
    .object({
    kind: zod_1.z.literal("date"),
    compareFrom: CompareCode.default(2),
    dateFrom: zod_1.z.string().optional(),
    condition: ConditionCode.default(1),
    compareTo: CompareCode.default(5),
    dateTo: zod_1.z.string().optional(),
})
    .refine((v) => v.dateFrom || v.dateTo, "Cần ít nhất một mốc ngày");
exports.NumberColumnFilterSchema = zod_1.z
    .object({
    kind: zod_1.z.literal("number"),
    numberFrom: zod_1.z.number().optional(),
    numberTo: zod_1.z.number().optional(),
})
    .refine((v) => v.numberFrom !== undefined || v.numberTo !== undefined, "Cần ít nhất một mốc số");
exports.ColumnFilterSchema = zod_1.z.union([
    exports.TextColumnFilterSchema,
    exports.ListColumnFilterSchema,
    exports.DateColumnFilterSchema,
    exports.NumberColumnFilterSchema,
]);
exports.ColumnFilterMapSchema = zod_1.z.record(zod_1.z.string(), exports.ColumnFilterSchema);
/**
 * Trên URL bộ lọc đi kèm dưới dạng JSON trong tham số `cf`. Chuỗi hỏng thì coi
 * như không lọc — lưới vẫn hiện dữ liệu thay vì trả lỗi 400 cho người dùng.
 */
exports.ColumnFilterQuery = zod_1.z
    .union([zod_1.z.string(), zod_1.z.record(zod_1.z.string(), zod_1.z.unknown())])
    .optional()
    .transform((v) => {
    if (!v)
        return {};
    let raw = v;
    if (typeof v === "string") {
        if (!v.trim())
            return {};
        try {
            raw = JSON.parse(v);
        }
        catch {
            return {};
        }
    }
    const parsed = exports.ColumnFilterMapSchema.safeParse(raw);
    return parsed.success ? parsed.data : {};
});
/** Nhãn tóm tắt hiện trong ô đầu cột khi bộ lọc đang bật, như hệ cũ. */
function columnFilterLabel(f) {
    switch (f.kind) {
        case "text":
            return f.value;
        case "list":
            return f.values.length === 1
                ? String(f.values[0] ?? "(trống)")
                : `${f.values.length} lựa chọn`;
        case "number": {
            const from = f.numberFrom === undefined ? "" : `≥ ${f.numberFrom}`;
            const to = f.numberTo === undefined ? "" : `≤ ${f.numberTo}`;
            return [from, to].filter(Boolean).join(" và ");
        }
        case "date": {
            const parts = [];
            if (f.dateFrom)
                parts.push(`${exports.COMPARE_LABEL[f.compareFrom]} ${f.dateFrom}`);
            if (f.dateTo)
                parts.push(`${exports.COMPARE_LABEL[f.compareTo]} ${f.dateTo}`);
            return parts.join(` ${exports.CONDITION_LABEL[f.condition].toLowerCase()} `);
        }
    }
}
